import * as Sentry from '@sentry/react-native';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';

import { Alert } from '@components/Alert';
import {
  Document,
  DocumentDetailFragmentDoc,
  Message,
  MessageDetailFragmentDoc,
  MessageProject,
  MessageTask,
  useCreateMessageIndividualMutation,
  useListDraftMessagesQuery,
  MessageDetailFragment,
  ListDraftDocumentsDocument,
  ListMessagesDocument,
  useUpdateMessageIndividualMutation,
} from '@graphql/generated';
import { insertPublishedMessageEdge } from '@hooks/chatProcessing/insertPublishedMessageEdge';
import { removeDraftMessageEntry } from '@hooks/chatProcessing/removeDraftMessageEntry';
import {
  allUploadsCompleted,
  hasPendingUploads,
  shouldPublishMessage,
} from '@hooks/chatProcessing/uploadState';
import { useApolloClient } from '@hooks/useApolloClient';
import useChatInput from '@hooks/useChatInput';
import { useChatUploadManager } from '@hooks/useChatUploadManager';
import { useNetInfo } from '@src/context/NetInfoContext';
import { mapDocumentsToLocalFiles } from '@utils/fileUtils';
import { filterActiveRefetchQueries } from '@utils/filterActiveRefetchQueries';
import { AsyncStorage } from '@utils/storage';
import {
  clearCachedBlobIds,
  hasRetriedBlobRecovery,
  listExistingLocalFileClientIds,
  markBlobRecoveryRetried,
  planBlobNotFoundRecovery,
} from '@utils/uploadRecovery';

import useMe from './useMe';

type MessageParams = Pick<
  Message,
  | 'body'
  | 'chatId'
  | 'clientId'
  | 'projectIds'
  | 'replyId'
  | 'replyMessage'
  | 'taskIds'
  | 'publish'
  | 'attachments'
  | 'failedAt'
  | 'checkin'
>;

type ChatProcessingContext = {
  retrySendFailedMessage: (clientId: string, authorId: string) => void;
};

const chatProcessingContext = createContext<ChatProcessingContext | undefined>(
  undefined
);

// TA-10162: ChatProcessingProvider can be mounted more than once (e.g. the
// web drawer alongside the main tree). Each instance watches the same
// listDraftMessages and races to send the same draft; the loser's
// createMessageIndividual comes back DUPLICATE_MESSAGE. The per-instance
// inFlight ref cannot see the other instance's sends, so the guard must be
// module-scoped. Entries are always released in onCompleted/onError.
const globalInFlightClientIds = new Set<string>();

export const ChatProcessingProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { Provider } = chatProcessingContext;
  const { client } = useApolloClient();
  const { me } = useMe();
  const { isConnected } = useNetInfo();

  const { data, refetch } = useListDraftMessagesQuery();
  const { t } = useTranslation();
  const {
    finalizeUploadedFiles,
    waitUntilNotUploading,
    invalidateUploadedBlob,
  } = useChatUploadManager();
  const { refreshActivity, setRefreshActivity } = useChatInput();

  // Refs for tracking messages — mutations must not trigger re-renders
  const messagesInProcessRef = useRef<string[]>([]);
  const processedClientIdsRef = useRef<Set<string>>(new Set());
  const [isProcessing, setIsProcessing] = useState(false);

  // Promise-chain mutex scoped to this provider instance so it cannot leak
  // across user sessions or survive unmount in a blocked state.
  const processingChainRef = useRef<Promise<void>>(Promise.resolve());

  const previousUserIdRef = useRef<string | undefined>(me?.id);

  useEffect(() => {
    const currentUserId = me?.id;

    if (currentUserId !== previousUserIdRef.current) {
      if (previousUserIdRef.current) {
        processedClientIdsRef.current = new Set();
        messagesInProcessRef.current = [];
        processingChainRef.current = Promise.resolve();
        setIsProcessing(false);
      }

      if (currentUserId) {
        refetch?.();
      }

      previousUserIdRef.current = currentUserId;
    }
  }, [me?.id, refetch]);

  // Reset the mutex on unmount so a blocked chain cannot leak to a remounted
  // provider (e.g. fast-refresh or re-login).
  useEffect(() => {
    return () => {
      processingChainRef.current = Promise.resolve();
    };
  }, []);

  // Acquire the processing mutex; returns a release function to call when done
  const acquireGlobalLock = (): Promise<() => void> => {
    let release!: () => void;
    const acquired = new Promise<void>((resolve) => {
      release = resolve;
    });
    const result = processingChainRef.current.then(() => release);
    processingChainRef.current = processingChainRef.current.then(
      () => acquired
    );
    return result;
  };

  // Enhanced error handling for mutations
  const [createMessageIndividual] = useCreateMessageIndividualMutation();

  const [updateMessageIndividual] = useUpdateMessageIndividualMutation();

  // TA-10162: this only releases the in-process slot — it must NOT remove the
  // listDraftMessages entry. That entry is the message's only render source
  // until the listMessages hook delivers the published message (retirement
  // lives in ChatMessageData's effect), and on send failures it is what keeps
  // the failed message visible for retry (handleFailedMessage marks failedAt
  // on the same clientId-keyed entity).
  const releaseInProcessSlot = (clientId: string) => {
    messagesInProcessRef.current = messagesInProcessRef.current.filter(
      (m) => m !== clientId
    );
  };

  // TA-10443: a duplicate rejection means the server already has this
  // clientId. The prior assumption ("a concurrent send owns this message and
  // its onCompleted retires the draft echo", TA-10162) fails when the winning
  // send belonged to a dead request or a previous app session: nothing ever
  // retires the server-persisted draft, so it comes back poisoned on every
  // launch and repeats the duplicate rejection forever (Sentry FRONTEND-291
  // "Message stuck in sending state" / FRONTEND-2CM "Client has already been
  // taken"). Verify against the server: if a published copy exists, land it
  // in the thread cache so ChatMessageData's retirement effect retires the
  // draft echo through the same safe path onCompleted uses. When no
  // published copy is found, a live concurrent send may still own it, so the
  // draft is kept exactly as before.
  const retirePublishedDuplicate = async (clientId: string, chatId: string) => {
    if (!client) return;
    try {
      // no-cache: this fetch is verification only. The deliberate cache
      // write happens through insertPublishedMessageEdge below, into the
      // open chat's own listMessages connection.
      const { data } = await client.query({
        query: ListMessagesDocument,
        variables: {
          chatId,
          includeDraft: false,
          includePublished: true,
          first: 30,
        },
        fetchPolicy: 'no-cache',
      });
      const publishedCopy = data?.listMessages?.edges?.find(
        (edge: { node?: { clientId?: string; publishedAt?: string } } | null) =>
          edge?.node?.clientId === clientId && edge?.node?.publishedAt
      )?.node;
      Sentry.addBreadcrumb({
        category: 'chat.duplicateSend',
        message: publishedCopy
          ? 'Duplicate send: published copy found, retiring draft echo'
          : 'Duplicate send: no published copy in recent messages, keeping draft',
        data: { clientId, chatId },
      });
      if (publishedCopy && client) {
        insertPublishedMessageEdge(client.cache, publishedCopy);
      }
    } catch (error) {
      // Verification is best-effort; on failure behavior degrades to the
      // pre-TA-10443 no-op, and the next send attempt retries the check.
      Sentry.captureException(error);
    }
  };

  const handleFailedMessage = (message: MessageParams) => {
    if (!me?.id) return;

    client?.cache.updateFragment<MessageDetailFragment>(
      {
        id: client.cache.identify({
          ...message,
          authorId: me?.id,
          __typename: 'Message',
        }),
        fragment: MessageDetailFragmentDoc,
        fragmentName: 'MessageDetail',
      },
      (data) => {
        return {
          ...data,
          failedAt: new Date().toISOString(),
          isRetry: true,
        };
      }
    );
  };

  const retrySendFailedMessage = (clientId: string, authorId: string) => {
    if (!client?.cache) return;
    if (!isConnected) {
      Alert.alert(
        t('models:chat.netInfoTitle'),
        t('models:chat.netInfoMessage')
      );
      return;
    }

    processedClientIdsRef.current.delete(clientId);

    const message = client.cache.updateFragment<MessageDetailFragment>(
      {
        id: client.cache.identify({
          clientId,
          authorId,
          __typename: 'Message',
        }),
        fragment: MessageDetailFragmentDoc,
        fragmentName: 'MessageDetail',
      },
      (data: any) => {
        return {
          ...data,
          createdAt: new Date().toISOString(),
          failedAt: null,
          isRetry: false,
        };
      }
    );
    processMessageInBackground(message);
  };

  const processMessageInBackground = async (message: any) => {
    const clientId = message.clientId;

    // Quick check before queuing
    if (
      messagesInProcessRef.current.includes(clientId) ||
      processedClientIdsRef.current.has(clientId) ||
      globalInFlightClientIds.has(clientId)
    ) {
      return;
    }

    const releaseLock = await acquireGlobalLock();

    try {
      // Double-check after acquiring lock
      if (
        messagesInProcessRef.current.includes(clientId) ||
        processedClientIdsRef.current.has(clientId) ||
        globalInFlightClientIds.has(clientId)
      ) {
        return;
      }

      processedClientIdsRef.current.add(clientId);
      messagesInProcessRef.current = [
        ...messagesInProcessRef.current,
        clientId,
      ];

      if (isConnected) {
        await sendDraftMessage(message);
      }
    } catch (error) {
      console.error('Error processing message:', error);
      Sentry.captureException(error);

      // Remove from processed set on error so it can be retried
      processedClientIdsRef.current.delete(clientId);
      throw error;
    } finally {
      releaseLock();
    }
  };

  // Sequential processing without delays to prevent flickering
  useEffect(() => {
    if (isConnected && data?.listDraftMessages?.length && !isProcessing) {
      const nonFailedDrafts = data?.listDraftMessages?.filter(
        (m) => m?.failedAt == null
      );
      // Process messages sequentially to prevent race conditions
      const processSequentially = async () => {
        if (nonFailedDrafts.length === 0) return;

        setIsProcessing(true);

        try {
          for (const message of nonFailedDrafts) {
            const clientId = message.clientId;

            // Skip if already processed or processing
            if (
              messagesInProcessRef.current.includes(clientId) ||
              processedClientIdsRef.current.has(clientId) ||
              globalInFlightClientIds.has(clientId)
            ) {
              continue;
            }

            try {
              await processMessageInBackground(message);
            } catch (error) {
              console.error('Error processing message:', clientId, error);
              // Continue with next message even if one fails
            }
            // No artificial delays - process as fast as possible
          }
        } finally {
          setIsProcessing(false);
        }
      };

      processSequentially();
    }
  }, [data?.listDraftMessages, isConnected, isProcessing]);

  const sendDraftMessage = useCallback(
    async (attributes: MessageParams) => {
      const cache = client?.cache;
      if (!cache) return;

      const {
        body,
        chatId,
        clientId,
        attachments,
        publish,
        replyId,
        replyMessage,
        checkin,
      } = attributes;

      const messageTasks = (attachments || []).filter(
        (item) => item.__typename === 'MessageTask'
      ) as MessageTask[];
      const taskIds = messageTasks.map((item) => item.task?.id);

      const messageProjects = (attachments || []).filter(
        (item) => item.__typename === 'MessageProject'
      ) as MessageProject[];
      const projectIds = messageProjects.map((item) => item.project?.id);

      const documents = (attachments || []).filter(
        (item) => item.__typename === 'Document'
      ) as Document[];

      // Wait for any pending uploads before sending to server.
      // Sending createMessageIndividual with body='' and documents=[] (no blobId yet)
      // causes the server to reject or create an empty draft that never gets published.
      const pendingDocuments = documents.filter(
        (item) => !item.blobId || item.blobId === ''
      );

      if (pendingDocuments.length > 0) {
        try {
          await waitUntilNotUploading(
            mapDocumentsToLocalFiles(pendingDocuments)
          );
        } catch (error) {
          // Upload timed out — mark message as failed so user can retry.
          // Keep the draft entry: it is what renders the failed message.
          Sentry.captureException(error);
          releaseInProcessSlot(clientId);
          handleFailedMessage(attributes);
          return;
        }
      }

      // Re-read blobIds from Apollo cache after uploads complete
      const uploadFinishedDocuments = documents
        .map((doc) => {
          if (doc.blobId && doc.blobId !== '') return doc;
          const cached = cache.readFragment<{ blobId: string }>({
            id: cache.identify({
              __typename: 'Document',
              clientId: doc.clientId,
            }),
            fragment: DocumentDetailFragmentDoc,
            fragmentName: 'DocumentDetail',
          });
          return cached?.blobId ? { ...doc, blobId: cached.blobId } : doc;
        })
        .filter((item) => item.blobId && item.blobId !== '');

      const documentsAttributes = uploadFinishedDocuments.map((doc) => ({
        blobId: doc.blobId,
        clientId: doc.clientId,
        contentType: doc.contentType ?? null,
        duration: doc.duration ?? null,
        folderId: null,
        isAudio: doc.isAudio,
        isImage: doc.isImage,
        name: doc.name,
        projectIds: messageProjects.map((item) => item.project?.id),
        taskIds: messageTasks.map((item) => item.task?.id),
      }));

      let newCheckin = null;
      if (checkin?.latitude) {
        const { __typename, id: __, withinGeofence: ___, ...rest } = checkin;
        newCheckin = { ...rest };
      }

      const params = {
        body: body,
        chatId,
        clientId,
        projectIds,
        replyId,
        replyMessage,
        taskIds,
        publish,
        checkin: newCheckin,
        documents: [...documentsAttributes],
      };

      const refetchQueries: string[] = [];
      if (projectIds.length > 0 && Platform.OS === 'web') {
        if (window.location.href?.includes('/globalActivity')) {
          refetchQueries.push('listActivities');
        }
        if (window.location.href?.includes('/projects/')) {
          refetchQueries.push(
            'listProjectDocuments',
            'listActivitiesByProject'
          );
        }
        if (taskIds.length > 0) {
          refetchQueries.push('listTaskDocuments', 'listActivitiesByTask');
        }
      } else if (projectIds.length > 0 || taskIds.length > 0) {
        refetchQueries.push('listActivities');
      }

      // TA-10233: string refetch names only match active observable queries;
      // inactive names (e.g. task panel closed) trigger Apollo unknown-query
      // warnings without refetching anything.
      const activeRefetchQueries = filterActiveRefetchQueries(
        client,
        refetchQueries
      );

      delete params.replyMessage;

      const storedId = 'MsgClientID' + clientId;

      // TA-10062: the blob is gone for good — stop the draft retrying forever.
      const retireUnsendableMessage = () => {
        void AsyncStorage.deleteItem(storedId);
        if (client) {
          removeDraftMessageEntry(client.cache, clientId);
        }
        client?.cache.updateQuery(
          {
            query: ListDraftDocumentsDocument,
            variables: { chatId },
          },
          (cacheData) => ({
            listDraftDocuments: (cacheData?.listDraftDocuments || []).filter(
              (d: Document) => d.messageClientId !== clientId
            ),
          })
        );
        finalizeUploadedFiles(mapDocumentsToLocalFiles(documents));
      };

      // TA-10885: see the matching handler in ChatMessageData. Only a file
      // that is genuinely gone from disk is terminal; a stale blobId from the
      // persisted dedup map is not.
      const recoverOrRetireBlobNotFound = async (sentDocuments: Document[]) => {
        const files = mapDocumentsToLocalFiles(sentDocuments);
        const plan = planBlobNotFoundRecovery({
          files,
          existingClientIds: await listExistingLocalFileClientIds(files),
          // Shared with ChatMessageData: one budget per message, whichever
          // path spends it.
          alreadyRetried: hasRetriedBlobRecovery(clientId),
        });

        if (plan.action === 'retire') {
          retireUnsendableMessage();
          return;
        }

        markBlobRecoveryRetried(clientId);
        await invalidateUploadedBlob(plan.files);
        if (client) {
          clearCachedBlobIds(client.cache, plan.files);
        }
      };

      // Guard against rapid re-entry before the first network round-trip
      // completes. Cleared in onCompleted / onError.
      if (globalInFlightClientIds.has(clientId)) {
        return;
      }
      globalInFlightClientIds.add(clientId);

      try {
        const isMsgCreated = await AsyncStorage.getItem(storedId);

        if (isMsgCreated && isMsgCreated === '1') {
          await updateMessageIndividual({
            variables: {
              clientId: clientId,
              attributes: {
                ...params,
                publish: shouldPublishMessage(
                  documents.length,
                  uploadFinishedDocuments.length
                ),
              },
            },
            refetchQueries: activeRefetchQueries,
            onError: (err) => {
              globalInFlightClientIds.delete(clientId);
              // Release the in-process slot on every error path, not just
              // onCompleted — otherwise clientId stays in
              // messagesInProcessRef forever, and the guards in
              // processMessageInBackground (background loop AND
              // retrySendFailedMessage) silently skip this message for the
              // rest of the provider's lifetime.
              releaseInProcessSlot(clientId);
              // TA-10062: this mutation also runs here — outside the chat
              // screen, e.g. finalizing an upload while the user has
              // navigated away — so it needs the same BLOB_NOT_FOUND
              // handling ChatMessageData.tsx applies to its own send path.
              // Without it, a deleted-by-another-user blob keeps failing
              // silently (captureException only) and the draft is never
              // retired, so it retries again on every relaunch/reconnect.
              // No toast here: this provider is mounted above ToastProvider
              // in CoreProviders, so useToast() has no context to read.
              if (
                err.graphQLErrors?.some(
                  (e) => e?.extensions?.code === 'BLOB_NOT_FOUND'
                )
              ) {
                // TA-10885: same stale-dedup-entry recovery as
                // ChatMessageData — a blobId reused from the persisted hash
                // map can name a blob the server dropped while the file is
                // still on the device. Forget it and leave the draft pending;
                // the chat screen's uploader re-uploads it the next time this
                // chat is open, instead of the file being lost silently here.
                void recoverOrRetireBlobNotFound(uploadFinishedDocuments);
                return;
              }
              console.error('UpdateMessageIndividual error:', err);
              Sentry.captureException(err);
            },
            onCompleted: (data) => {
              globalInFlightClientIds.delete(clientId);
              // TA-10162: land the confirmed message in the thread before the
              // draft echo is removed — see insertPublishedMessageEdge.
              if (client) {
                insertPublishedMessageEdge(
                  client.cache,
                  data.updateMessageIndividual
                );
              }
              // TA-10162: the draft entry in listDraftMessages is NOT removed
              // in mutation callbacks — that races the listMessages hook's
              // broadcast and blanks the message out of the open thread.
              // ChatMessageData's retirement effect removes it once the hook
              // actually delivers the published message.
              releaseInProcessSlot(data.updateMessageIndividual.clientId);

              markMessageDocument(data.updateMessageIndividual);
              if (
                allUploadsCompleted(
                  documents.length,
                  uploadFinishedDocuments.length
                )
              ) {
                finalizeUploadedFiles(mapDocumentsToLocalFiles(documents));
              }
            },
          });
          return;
        }

        await createMessageIndividual({
          variables: {
            attributes: {
              ...params,
              publish: shouldPublishMessage(
                documents.length,
                uploadFinishedDocuments.length
              ),
            },
          },
          refetchQueries: activeRefetchQueries,
          onError: (err) => {
            globalInFlightClientIds.delete(clientId);

            // Check for duplicate errors using the standardised DUPLICATE_MESSAGE
            // extension code (TA-9385). Keep the message fallback as a safety net
            // during rollout; remove once the backend change is fully deployed.
            const isDuplicateError =
              err.graphQLErrors?.some(
                (e) => e.extensions?.code === 'DUPLICATE_MESSAGE'
              ) || err.message.includes('Client has already been taken');

            if (isDuplicateError) {
              // Treat duplicate as success - message was already created.
              // TA-10162: do NOT remove the draft entry here — a duplicate
              // means a concurrent send (another provider instance) owns this
              // message, and ITS onCompleted decides when the draft echo can
              // be retired. Removing it here blanked the message out of the
              // thread until the winning mutation's response landed.
              // TA-10443: but when that owner is a dead request or a past
              // session, nothing ever retires the draft — verify against the
              // server and hand a confirmed published copy to the safe
              // retirement path.
              releaseInProcessSlot(clientId);
              void retirePublishedDuplicate(clientId, chatId);
              return;
            }

            // Handle other errors normally. Keep the draft entry: it is what
            // renders the failed message so the user can retry (TA-10162).
            console.error('CreateMessageIndividual error:', err);
            releaseInProcessSlot(clientId);
            handleFailedMessage(params);
          },
          onCompleted: (data) => {
            globalInFlightClientIds.delete(clientId);
            if (refetchQueries.find((item) => item === 'listActivities')) {
              setTimeout(() => {
                !refreshActivity && setRefreshActivity(true);
              }, 500);
            }
            // TA-10162: land the confirmed message in the thread before the
            // draft echo is removed — see insertPublishedMessageEdge.
            if (client) {
              insertPublishedMessageEdge(
                client.cache,
                data.createMessageIndividual
              );
            }
            // TA-10162: the draft entry in listDraftMessages is NOT removed
            // in mutation callbacks — that races the listMessages hook's
            // broadcast (the hook can still be serving its last complete
            // snapshot WITHOUT this message when the response lands, since
            // the draft edge keeps the read incomplete) and blanks the
            // message out of the open thread until the hook catches up.
            // ChatMessageData's retirement effect removes the entry once the
            // hook actually delivers the published message.
            releaseInProcessSlot(data.createMessageIndividual.clientId);
            const hasPendingDocuments = hasPendingUploads(
              documents.length,
              uploadFinishedDocuments.length
            );
            if (hasPendingDocuments) {
              AsyncStorage.setItem(storedId, '1');
            }
            markMessageDocument(
              data.createMessageIndividual,
              hasPendingDocuments
            );
            if (
              allUploadsCompleted(
                documents.length,
                uploadFinishedDocuments.length
              )
            ) {
              finalizeUploadedFiles(mapDocumentsToLocalFiles(documents));
            }
          },
        });
      } catch (error) {
        globalInFlightClientIds.delete(clientId);
        console.error('Error in sendDraftMessage:', error);
        throw error;
      }
    },
    [me?.id, me?.name, me?.avatar?.path]
  );

  const markMessageDocument = (message: any, forceMark = false) => {
    if (forceMark || message.isDraft) {
      client?.cache.updateQuery(
        {
          query: ListDraftDocumentsDocument,
          variables: {
            chatId: message.chatId,
          },
        },
        (data) => {
          if (!data?.listDraftDocuments) return data;
          return {
            listDraftDocuments: data.listDraftDocuments.map((doc: any) =>
              doc.messageClientId === message.clientId && !doc.messageCreateYn
                ? { ...doc, messageCreateYn: true }
                : doc
            ),
          };
        }
      );
    }
  };

  return (
    <Provider
      value={
        {
          retrySendFailedMessage,
        } as ChatProcessingContext
      }>
      {children}
    </Provider>
  );
};

const useChatProcessing = (): ChatProcessingContext => {
  const context = useContext(chatProcessingContext);
  if (context === undefined) {
    throw new Error('useChatProcessing must be used within a Provider');
  }
  return context;
};

export default useChatProcessing;
